import React from 'react';
import { motion } from 'framer-motion';
import { FaChevronRight } from 'react-icons/fa';
import makeIcon from '../assets/logos/make.png';
import aikoLogo from '../assets/logos/aiko.png';
import figmaLogo from '../assets/logos/figma.png';
import newbiLogo from '../assets/logos/newbi.png';
import reactLogo from '../assets/logos/react.png';

// Importez les autres logos ici une fois que vous les aurez ajoutés
// import seoLogo from '../assets/logos/seo.png';

const InvoiceCard = ({ logoSrc, title, description, price, details }) => {
  return (
    <motion.div 
      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center mb-2">
          <div className="w-16 h-16 flex items-center justify-center mr-3">
            {logoSrc ? (
              <img src={logoSrc} alt={title} className="w-16 h-16 object-contain" />
            ) : (
              <img src={aikoLogo} alt="aiko" className="w-16 h-16 object-contain" />
            )}
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="text-3xl font-bold text-gray-800 mb-1">{price}</div>
            <div className="text-sm text-gray-500">Forfait de base</div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" fill="#ccc"/>
              <path d="M14 17H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" fill="#ccc"/>
            </svg>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="grid grid-cols-2 mb-2">
            <div className="text-gray-500 text-sm">Service</div>
            <div className="text-gray-800 font-medium">{title}</div>
          </div>
          
          <div className="grid grid-cols-2 mb-2">
            <div className="text-gray-500 text-sm">Détails</div>
            <div className="text-gray-800">{details}</div>
          </div>
          
          <div className="grid grid-cols-2 mb-2">
            <div className="text-gray-500 text-sm">Mémo</div>
            <div className="text-gray-600 text-sm">{description}</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const StartupSection = () => {
  const leftColumnServices = [
    {
      logoSrc: reactLogo,
      title: "Sites internet & Landing pages",
      description: "Création de sites vitrines professionnels et de landing pages optimisées pour la conversion.",
      details: "HTML, CSS, JavaScript",
      price: "à partir de 890€"
    },
    {
      logoSrc: makeIcon,
      title: "Automatisation avec Make",
      description: "Optimisez vos processus métier grâce à l'automatisation et aux workflows personnalisés.",
      details: "Configuration, intégration, maintenance",
      price: "Sur devis"
    }
  ];

  const rightColumnServices = [
    {
      logoSrc: figmaLogo,
      title: "Création de logo",
      description: "Conception d'identités visuelles uniques qui reflètent les valeurs de votre entreprise.",
      details: "3 propositions, fichiers sources inclus",
      price: "à partir de 250 €"
    },
    {
      logoSrc: newbiLogo,
      title: "Rédaction d'articles SEO",
      description: "Contenu optimisé pour les moteurs de recherche qui améliore votre visibilité en ligne.",
      details: "Recherche de mots-clés, rédaction, optimisation",
      price: "Sur devis"
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 md:px-8 lg:px-16">
        {/* Conteneur supérieur avec le titre et la description */}
        <div className="w-full mb-16">
          <motion.span 
            className="inline-block text-indigo-600 font-medium mb-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Nos tarifs
          </motion.span>
          
          <motion.h2 
            className="text-2xl md:text-3xl lg:text-4xl font-medium mb-6 text-[#0a2540] leading-tight max-w-md"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Des solutions adaptées à votre budget
          </motion.h2>
          
          <motion.p 
            className="text-gray-600 mb-8 max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Chez aiko, nous proposons des tarifs transparents et adaptés à vos besoins. Que vous soyez une startup en pleine croissance 
            ou une entreprise établie, nos forfaits sont conçus pour vous offrir le meilleur rapport qualité-prix. Découvrez ci-dessous 
            nos services et leurs tarifs indicatifs. N'hésitez pas à nous contacter pour un devis personnalisé.
          </motion.p>
        </div>
        
        {/* Disposition asymétrique avec deux colonnes */}
        <div className="flex flex-col md:flex-row gap-12 md:gap-0">
          {/* Colonne de gauche (décalée vers le bas) */}
          <div className="md:w-1/2 md:pr-4 md:mt-32">
            <div className="space-y-8">
              {leftColumnServices.map((service, index) => (
                <div key={`left-${index}`} className="mb-8">
                  <InvoiceCard 
                    logoSrc={service.logoSrc}
                    title={service.title}
                    description={service.description}
                    details={service.details}
                    price={service.price}
                  />
                </div>
              ))}
            </div>
          </div>
          
          {/* Colonne de droite */}
          <div className="md:w-1/2 md:pl-4">
            <div className="space-y-8">
              {rightColumnServices.map((service, index) => (
                <div key={`right-${index}`} className="mb-8">
                  <InvoiceCard 
                    logoSrc={service.logoSrc}
                    title={service.title}
                    description={service.description}
                    details={service.details}
                    price={service.price}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StartupSection;